import { Component, ElementRef, Renderer2, effect, inject, input, viewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import * as QrCode from 'qrcode';

import { GenerationType, QrCodeOptions } from './qr-code.models';
import { QrCodeHelper } from './qr-code.static';

@Component({
  selector: 'ml-qr-code',
  template: ` <div [innerHtml]="innerHtml" #qrcElement></div> `,
  styles: [],
  standalone: true,
})
export class QrCodeComponent {
  private readonly renderer = inject(Renderer2);
  private readonly sanitizer = inject(DomSanitizer);

  readonly value = input.required<string>();
  readonly options = input.required<QrCodeOptions>();

  readonly qrElement = viewChild.required<ElementRef>('qrcElement');

  public innerHtml: SafeHtml;

  constructor() {
    effect(() => {
      const value = this.value();
      const options = this.options();
      if (!value) {
        throw new Error('[NgQrGen] value is null, implement: value="string value"');
      }

      if (!options) {
        throw new Error('[NgQrGen] options were null, implement: [options]="options" where options: QrGenOptions ');
      }

      this.generateQR(options);
    });
  }

  private generateQR(options: QrCodeOptions): void {
    if (options.type === GenerationType.Image) {
      this.renderImage(options);
    }
    if (options.type === GenerationType.Canvas) {
      this.renderCanvas(options);
    }
    if (options.type === GenerationType.Svg) {
      this.renderSvg(options);
    }
  }

  private renderCanvas(options: QrCodeOptions): void {
    const element: Element = this.renderer.createElement('canvas');
    void new Promise((resolve, reject) => {
      QrCode.toCanvas(element, this.value(), options, (error: Error) => {
        if (error) {
          reject(error);
          throw error;
        } else {
          resolve('success');
        }
      });
    }).then(() => {
      this.renderElement(element);
    });
  }

  private renderImage(options: QrCodeOptions): void {
    void QrCodeHelper.generateEncodedDataUrl(this.value(), options).then((url: string) => {
      const element: Element = this.renderer.createElement('img');
      element.setAttribute('src', url);
      this.renderElement(element);
    });
  }

  private renderSvg(options: QrCodeOptions): void {
    void QrCodeHelper.generateString(this.value(), options).then((generatedString: string) => {
      this.innerHtml = this.sanitizer.bypassSecurityTrustHtml(generatedString);
    });
  }

  private renderElement(element: Element): void {
    for (const node of this.qrElement().nativeElement.childNodes) {
      this.renderer.removeChild(this.qrElement().nativeElement, node);
    }
    this.renderer.appendChild(this.qrElement().nativeElement, element);
  }
}
